import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { BuildingCreateRequest } from '../../../../types';
import { createBuilding } from '../../../../services/api/endpoints/buildings';
import { useAuth } from '../../../../contexts/AuthContext';

interface BuildingFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (buildingId: string) => void;
}

// Common US timezones - matching backend Timezone enum
const TIMEZONES = [
  'UTC',
  'EST',
  'EDT',
  'CST',
  'CDT',
  'MST',
  'MDT',
  'PST',
  'PDT',
  'AKST',
  'AKDT',
  'HST',
];

const BuildingFormModal: React.FC<BuildingFormModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    timezone: 'EST',
    latitude: 0,
    longitude: 0,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setFormData({
        name: '',
        address: '',
        timezone: 'EST',
        latitude: 0,
        longitude: 0,
      });
      setErrors({});
      setApiError(null);
    }
  }, [open]);

  const handleChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  const handleSelectChange = (field: string) => (
    event: SelectChangeEvent<string>
  ) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });
    // Clear error for this field
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: '',
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Building name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Name must be 255 characters or less';
    }

    // Address validation
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    } else if (formData.address.trim().length < 10) {
      newErrors.address = 'Please enter a complete street address';
    } else if (formData.address.length > 500) {
      newErrors.address = 'Address must be 500 characters or less';
    }

    // Timezone validation
    if (!formData.timezone) {
      newErrors.timezone = 'Timezone is required';
    }

    // Latitude validation
    if (formData.latitude === 0 && formData.longitude === 0) {
      newErrors.latitude = 'Please enter valid coordinates (0,0 is not a valid location)';
    } else if (formData.latitude < -90 || formData.latitude > 90) {
      newErrors.latitude = 'Latitude must be between -90 and 90';
    }

    // Longitude validation
    if (formData.latitude === 0 && formData.longitude === 0) {
      newErrors.longitude = 'Please enter valid coordinates';
    } else if (formData.longitude < -180 || formData.longitude > 180) {
      newErrors.longitude = 'Longitude must be between -180 and 180';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    if (!user?.token) {
      setApiError('No authentication token available');
      return;
    }

    try {
      setIsSaving(true);
      setApiError(null);

      const payload: BuildingCreateRequest = {
        name: formData.name.trim(),
        address: formData.address.trim(),
        timezone: formData.timezone,
        latitude: Number(formData.latitude),
        longitude: Number(formData.longitude),
      };

      const newBuilding = await createBuilding(user.token, payload);
      onSuccess(newBuilding.id);
    } catch (err: any) {
      console.error('Error creating building:', err);
      setApiError(err.message || 'Failed to create building');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="building-form-modal-title"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '90%', md: '600px' },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Typography id="building-form-modal-title" variant="h6" component="h2">
            Create New Building
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={isSaving}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Building Name"
            value={formData.name}
            onChange={handleChange('name')}
            error={!!errors.name}
            helperText={errors.name || 'Required'}
            disabled={isSaving}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Address"
            value={formData.address}
            onChange={handleChange('address')}
            error={!!errors.address}
            helperText={errors.address || 'Required - Full street address'}
            disabled={isSaving}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="timezone-select-label">Timezone</InputLabel>
            <Select
              labelId="timezone-select-label"
              value={formData.timezone}
              label="Timezone"
              onChange={handleSelectChange('timezone')}
              disabled={isSaving}
              error={!!errors.timezone}
            >
              {TIMEZONES.map((tz) => (
                <MenuItem key={tz} value={tz}>
                  {tz}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              type="number"
              label="Latitude"
              value={formData.latitude}
              onChange={handleChange('latitude')}
              error={!!errors.latitude}
              helperText={errors.latitude || 'Between -90 and 90'}
              disabled={isSaving}
              inputProps={{ step: '0.000001' }}
            />

            <TextField
              fullWidth
              type="number"
              label="Longitude"
              value={formData.longitude}
              onChange={handleChange('longitude')}
              error={!!errors.longitude}
              helperText={errors.longitude || 'Between -180 and 180'}
              disabled={isSaving}
              inputProps={{ step: '0.000001' }}
            />
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Tip: You can find coordinates by searching the address on Google Maps, 
            right-clicking the location, and selecting the coordinates to copy them.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSaving}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Create Building'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default BuildingFormModal;
